/**
 * Google sign-in, from the portal's side.
 *
 * There is almost nothing here, which is the point: the flow is two redirects
 * between the browser and our own server (`/api/auth/google/start` and
 * `/api/auth/google/callback`), so the portal starts it with a navigation and
 * reads the outcome off the query string when the server sends the browser
 * back. No SDK, no project key, and nothing about Google baked into the bundle
 * at build time — which is what lets one built image serve every environment.
 *
 * Everything the flow needs to remember is handed to the server on the way in
 * and kept in a signed cookie, not carried through Google in the URL.
 */
import { AUTH_REQUEST_PARAM } from '@mindpeer/auth-shared';

const START_PATH = '/api/auth/google/start';

export interface GoogleSignInParams {
  /** Where to land afterwards, when no module is waiting. */
  next?: string | null;
  /** The module authorization request this sign-in is standing inside. */
  handle?: string | null;
  /** Whether the user asked to stay signed in. */
  rememberMe?: boolean;
  /** Pre-fills the account chooser when we already know the address. */
  email?: string | null;
}

/**
 * Hand the browser to the server to begin a sign-in.
 *
 * `assign`, not `replace`: coming back from a cancelled Google sign-in should
 * leave the portal's own page in history where the user expects it.
 */
export function startGoogleSignIn(params: GoogleSignInParams = {}): void {
  const url = new URL(START_PATH, window.location.origin);
  if (params.next) url.searchParams.set('next', params.next);
  if (params.handle) url.searchParams.set(AUTH_REQUEST_PARAM, params.handle);
  if (params.rememberMe) url.searchParams.set('remember', '1');
  if (params.email) url.searchParams.set('email', params.email);
  window.location.assign(url.toString());
}

/**
 * What the server says went wrong, phrased for the person reading it.
 *
 * Every failure inside the flow ends as a redirect back to a portal screen
 * with `?error=<code>`; anything unrecognised falls back to the generic
 * message rather than showing a raw code.
 */
const MESSAGES: Record<string, string> = {
  google_cancelled: 'Google sign-in was cancelled. You can try again, or use your password below.',
  google_expired: 'That sign-in took too long to finish. Please try again.',
  google_unverified:
    'Google has not verified that email address, so it cannot be used to sign in. Verify it with Google first, or sign in with a password.',
  google_unavailable:
    'Google sign-in is not available right now. Please sign in with your email and password.',
  account_suspended: 'Your account has been suspended. Contact support@mindpeer.com for help.',
};

const GENERIC = 'Could not complete sign-in with Google. Please try again.';

/** Read the outcome of a returned-from-Google navigation, if there was one. */
export function googleErrorFrom(search: string): string | null {
  const code = new URLSearchParams(search).get('error');
  if (!code) return null;
  return MESSAGES[code] ?? GENERIC;
}
